import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { subscribeToProducts, getInitialProducts } from '../../services/productService';
import { getProductImg, handleImgError } from '../../utils/imageHelper';

const QuickSearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [productsList, setProductsList] = useState(getInitialProducts);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = subscribeToProducts((latest) => {
      setProductsList(latest);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
      setQuery('');
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const filteredResults = query.trim()
    ? productsList
        .filter(
          (p) =>
            p.productName.toLowerCase().includes(query.toLowerCase()) ||
            p.category.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 6)
    : [];

  const handleSelectProduct = (id) => {
    onClose();
    navigate(`/shop/${id}`);
  };

  const handleCategoryClick = (cat) => {
    onClose();
    navigate(`/shop?category=${cat}`);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="quick-search__backdrop" onClick={onClose}>
          <motion.div
            className="quick-search__card"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -15, scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            <div className="quick-search__header">
              <i className="ri-search-line quick-search__icon"></i>
              <input
                ref={inputRef}
                type="text"
                className="quick-search__input"
                placeholder="Search atelier pieces, sofas, chairs, horology..."
                aria-label="Search atelier pieces, sofas, chairs, horology"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button
                  type="button"
                  className="quick-search__clear-btn"
                  onClick={() => setQuery('')}
                  aria-label="Clear query"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
              <button
                type="button"
                className="quick-search__close-btn"
                onClick={onClose}
                aria-label="Close search"
              >
                ESC
              </button>
            </div>

            <div className="quick-search__body">
              {query.trim() === '' ? (
                <div className="quick-search__suggestions">
                  <span className="quick-search__label">Popular Collections</span>
                  <div className="quick-search__tags">
                    <button type="button" onClick={() => handleCategoryClick('sofa')}>
                      Sofas & Couches
                    </button>
                    <button type="button" onClick={() => handleCategoryClick('chair')}>
                      Armchairs
                    </button>
                    <button type="button" onClick={() => handleCategoryClick('table')}>
                      Tables & Consoles
                    </button>
                    <button type="button" onClick={() => handleCategoryClick('lighting')}>
                      Designer Lighting
                    </button>
                    <button type="button" onClick={() => handleCategoryClick('decor')}>
                      Home Accents
                    </button>
                  </div>
                </div>
              ) : filteredResults.length > 0 ? (
                <div className="quick-search__results">
                  <span className="quick-search__label">
                    Matching Pieces ({filteredResults.length})
                  </span>
                  <div className="quick-search__list">
                    {filteredResults.map((item) => (
                      <div
                        key={item.id}
                        className="quick-search__item"
                        onClick={() => handleSelectProduct(item.id)}
                      >
                        <img
                          src={getProductImg(item)}
                          onError={(e) => handleImgError(e, item)}
                          alt={item.productName}
                          className="quick-search__thumb"
                        />
                        <div className="quick-search__meta">
                          <span className="quick-search__cat">{item.category}</span>
                          <h6 className="quick-search__title">{item.productName}</h6>
                        </div>
                        <span className="quick-search__price">${item.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="quick-search__empty text-center py-4">
                  <i className="ri-search-eye-line fs-2 text-muted mb-2 d-block"></i>
                  <p className="m-0 text-muted">No pieces found matching "{query}"</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default QuickSearchModal;
